import { BulkTransactionColumn } from '../../bulk-transaction/bulk-transaction.projection'
import { rawValueFrom } from '../../edit-transaction/format-field-value'
import { ValuePickerOption } from '../value-picker/value-picker'

/**
 * The clipboard round trip for the Bulk Transactions grid.
 *
 * Two references, and they agree: the prototype's own grid and the existing bulk Create/Edit
 * modal's `CopyService` / `PasteService`. The wire format is Excel's — tabs across, newlines down —
 * so a block lifted from here pastes into a spreadsheet and a block copied out of one pastes back.
 *
 * Pure: no DOM, no ag-grid, no Angular. Which cells are selected is ag-grid's question (the dialog
 * asks `getCellRanges()`); what a block MEANS is this file's.
 */

/**
 * Clipboard text as a block: rows down, tabs across.
 *
 * `\r` is stripped rather than treated as a line break — Excel on Windows writes `\r\n` and a naive
 * split on `\r` would double every row. A single trailing newline is dropped, since a copied block
 * usually carries one and it is not a sixth empty row.
 */
export function parseClipboardGrid(text: string): string[][] {
  const normalised = text.replace(/\r\n?/g, '\n').replace(/\n$/, '')
  if (normalised === '') return []
  return normalised.split('\n').map(line => line.split('\t'))
}

/** One row, one column — the "fill the selection with this" case. */
export function isSingleValue(grid: readonly string[][]): boolean {
  return grid.length === 1 && grid[0].length === 1
}

/** The format a spreadsheet reads back. `\r\n` between rows, as `CopyService` writes it. */
export function toClipboardText(block: readonly string[][]): string {
  const lines = block.map(line => line.join('\t'))
  return lines.length > 1 ? lines.join('\r\n') : lines[0] ?? ''
}

/** Whether a column accepts a pasted value at all. */
function isPastableColumn(column: BulkTransactionColumn): boolean {
  return column.kind !== 'readOnly' && column.kind !== 'collection'
}

/**
 * A pasted value in the form the cell would have been typed with, or undefined when the column
 * cannot hold it.
 *
 * Refusing rather than coercing is the existing modal's rule (`allowUpdateBasedOnType`): a word
 * pasted over a date column leaves that cell alone instead of blanking a value that was correct.
 * Whole columns of a wider block can therefore be skipped while the rest of it lands, which is what
 * makes pasting a spreadsheet's export useful at all.
 *
 * The display-style rules do the parsing — `rawValueFrom` already accepts both the ISO form the API
 * sends and the US form the grid shows — so this only has to judge whether they got anywhere.
 */
export function coercePastedValue(
  raw: string,
  column: BulkTransactionColumn,
  options: readonly ValuePickerOption[],
): string | undefined {
  if (!isPastableColumn(column)) return undefined
  const text = raw.trim()
  // Clearing a cell is a legal paste: an empty column of the block empties the column it lands on.
  if (text === '') return ''

  if (column.kind === 'enum') {
    const match =
      options.find(option => option.value === text) ??
      options.find(
        option => option.text.toLowerCase() === text.toLowerCase(),
      ) ??
      options.find(option => option.value.toLowerCase() === text.toLowerCase())
    return match?.value
  }

  // Judged by the column's KIND, through the style rule that knows that kind — never through the
  // field's own `style`, which says how a value is displayed and not what kind of value it is. A
  // number-typed field styled `default` is still a number column, and asking `default.toRaw` (which
  // returns its input untouched) whether a word is a number would accept every word.
  if (column.kind === 'number') {
    // `prettyNumber` parses grouped digits and accounting parentheses; the plain numeric string it
    // yields is then readable by whatever style this field actually wears.
    const numeric = rawValueFrom(text, 'prettyNumber')
    return typeof numeric === 'number' ? String(numeric) : undefined
  }

  if (column.kind === 'date') {
    // `dateUS` accepts the ISO form the API sends and the US form the grid shows, and hands back
    // anything it could not read unchanged.
    const iso = rawValueFrom(text, 'dateUS')
    return typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)
      ? iso
      : undefined
  }

  return text
}
